import { useFormat } from 'helpers/hooks/useFormat';
import { useParams } from 'next/navigation';
import React from 'react';
import { Category } from 'shared/types/product/Category';
import { ProjectConfig } from 'types/project-config';

interface CategoryMenuProps {
  categories: Category[];
  activeCategory: Category;
  projectConfig?: ProjectConfig;
}

const CategoryMenu: React.FC<CategoryMenuProps> = ({ categories, activeCategory }) => {
  const { locale } = useParams();
  const { formatMessage: formatMessagePadi } = useFormat({ name: 'padi' });
  const { formatMessage: formatMessageProduct } = useFormat({ name: 'product' });
  const courseCategorySlugs = ['courses', 'experience-level', 'course-type', 'activity-type'];

  const activeClass = 'font-bold text-padi-gray-darker';
  const nonActiveClass = 'hover:text-padi-gray-darker';
  const padiBlueLink = 'text-padi-blue hover:text-padi-blue-darker';

  return (
    <div data-id="category-menu">
      <h3 className="sr-only" data-id="category-menu-heading">
        {formatMessageProduct({
          id: 'categories',
          defaultMessage: 'Categories',
        })}
      </h3>
      {categories.map(
        (category, index) =>
          category?.slug &&
          courseCategorySlugs.includes(category?.slug) && (
            <div key={`${category.name}-${index}`} className="pb-20" data-id={`category-item-${index}`}>
              <h3 className="mb-10 text-base font-bold" data-id={`category-heading-${index}`}>
                <a
                  href={`/${locale}${category._url}`}
                  className={activeCategory?.slug === category.slug ? activeClass : nonActiveClass}
                  data-id={`category-link-${index}`}
                >
                  {category.slug === 'courses'
                    ? formatMessagePadi({
                        id: 'allCourses',
                        defaultMessage: 'All Courses',
                      })
                    : category.name}
                </a>
              </h3>
              <ul role="list" className="space-y-8 text-sm text-gray-900" data-id={`category-subcategories-${index}`}>
                {category?.subCategories &&
                  category?.subCategories.map((subCategory, subIndex) => (
                    <li
                      key={`${subCategory.name}-${subIndex}`}
                      className="pb-5"
                      data-id={`subcategory-item-${index}-${subIndex}`}
                    >
                      <a
                        href={`/${locale}${subCategory._url}`}
                        className={activeCategory?.slug === subCategory.slug ? activeClass : nonActiveClass}
                        data-id={`subcategory-link-${index}-${subIndex}`}
                      >
                        {subCategory.name}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          ),
      )}
      <div className="pb-20" data-id="padi-club-section">
        <h3 className="mb-10 text-base font-bold" data-id="padi-club-heading">
          {formatMessagePadi({
            id: 'padiClub',
            defaultMessage: 'PADI Club',
          })}
        </h3>
        <ul role="list" className="space-y-4 text-sm text-gray-900" data-id="padi-club-list">
          <li className="pb-5" data-id="padi-club-discount-item">
            <a
              href={`/${locale}/product-definition/promotion-product-definition/promotion-club-discount/`}
              className={activeCategory?._url?.includes('promotion-club-discount') ? activeClass : nonActiveClass}
              data-id="padi-club-discount-link"
            >
              {formatMessagePadi({
                id: 'clubDiscountEligible',
                defaultMessage: 'Club Discount Eligible',
              })}
            </a>
          </li>
          <li className="pb-5" data-id="padi-club-learn-item">
            <a href={`/${locale}/padi-club/`} className={padiBlueLink} data-id="padi-club-learn-link">
              {formatMessagePadi({
                id: 'learnAboutPadiClub',
                defaultMessage: 'Learn about PADI Club',
              })}
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CategoryMenu;
